import type { DungeonInput, DungeonResult, DungeonFloor } from '@creanizer/types'

/**
 * ダンジョンジェネレータ固有のエラーメッセージを返す
 */
export function handleDungeonGeneratorError(error: Error, input: DungeonInput): string {
  // トークン制限エラー
  if (error.message.includes('token') || error.message.includes('too long')) {
    if (input.floors && input.floors > 5) {
      return 'フロア数が多すぎます。5フロア以下に減らしてください。'
    }
    if (input.mode === 'detailed') {
      return '詳細モードのデータが大きすぎます。標準モードをお試しください。'
    }
    return '生成内容が長すぎます。設定を簡略化してお試しください。'
  }
  
  // JSONパースエラー
  if (error.message.includes('JSON')) {
    if (input.mode === 'batch') {
      return 'バッチ生成でエラーが発生しました。生成数を減らしてお試しください。'
    }
    return 'ダンジョンデータの解析に失敗しました。もう一度お試しください。'
  }
  
  // レート制限
  if (error.message.includes('429') || error.message.includes('rate')) {
    return 'APIの利用制限に達しました。しばらく待ってからお試しください。'
  }
  
  // デフォルト
  return `エラーが発生しました: ${error.message}`
}

/**
 * 不完全なダンジョンデータでも最低限の情報を抽出
 */
export function salvagePartialDungeon(data: any): Partial<DungeonResult> | null {
  const result: Partial<DungeonResult> = {}
  
  // 基本情報だけでも取得
  if (data.basicInfo?.name) {
    result.basicInfo = data.basicInfo
  }
  
  // フロア情報の部分取得
  if (data.structure?.floors && Array.isArray(data.structure.floors)) {
    const validFloors = data.structure.floors.filter((f: any) => 
      f.level !== undefined && f.rooms && Array.isArray(f.rooms)
    )
    if (validFloors.length > 0) {
      result.structure = {
        ...data.structure,
        floors: validFloors,
        totalRooms: validFloors.reduce((sum: number, f: any) => sum + (f.rooms?.length || 0), 0)
      }
    }
  }
  
  // 最低限の情報があれば返す
  if (result.basicInfo && result.structure) {
    return result as DungeonResult
  }
  
  return null
}

/**
 * ダンジョンデータのメモリ使用量を推定
 */
export function estimateDungeonMemory(dungeon: DungeonResult): number {
  const floorCount = dungeon.structure?.floors?.length || 0
  const roomCount = dungeon.structure?.totalRooms || 0
  const trapCount = dungeon.contents?.traps?.length || 0
  const enemyCount = dungeon.contents?.enemies?.length || 0
  
  // 概算: 各要素のメモリ使用量を推定 (バイト単位)
  return floorCount * 5000 + roomCount * 1000 + trapCount * 500 + enemyCount * 500
}

/**
 * フロア間の接続を検証
 */
export function validateFloorConnections(dungeon: DungeonResult): string[] {
  const errors: string[] = []
  // 新しい構造では、階層間の接続は部屋の説明文に含まれるため、
  // この検証は不要になりました
  
  return errors
}

/**
 * ダンジョン構造の基本的な検証
 */
export function validateDungeonStructure(dungeon: any): boolean {
  if (!dungeon?.basicInfo?.name) return false
  if (!dungeon?.structure?.floors) return false
  if (!Array.isArray(dungeon.structure.floors)) return false
  if (dungeon.structure.floors.length === 0) return false
  
  // 各フロアが必要な構造を持っているか確認
  return dungeon.structure.floors.every((floor: any) => 
    floor.level !== undefined &&
    floor.theme &&
    Array.isArray(floor.rooms) &&
    floor.rooms.length > 0
  )
}

/**
 * 部分的なストリーミングデータからフロア数を推定
 */
export function countFloorsInPartialData(partialContent: string): number {
  const matches = partialContent.match(/"level"\s*:/g)
  return matches ? matches.length : 0
}

/**
 * JSONレスポンスのクリーニング（Geminiからの不完全な応答への対処）
 */
export function cleanJsonResponse(text: string): string {
  // コードブロックを除去
  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/)
  const jsonText = codeBlockMatch ? codeBlockMatch[1] : text
  
  // 前後の空白文字を除去
  return jsonText.trim()
}